import React from 'react'
import { useSelector } from 'react-redux'

import { selectDisplayScreens } from '@store/actionSlices/multiScreen'
import { DisplayScreen } from '@store/types'

import { CheckSvg, ChevronSvg, CollapseSvg, MonitorSvg } from '@svg/react'

import {
  checkIconProps,
  chevronIconProps,
  monitorIconProps,
} from '../../constants'
import { DisplayScreenStateMap } from '../../types'
import DisplayScreenActions from './DisplayScreenActions'

interface ExpandedPanelProps {
  mirroringScreens: DisplayScreenStateMap
  openSections: DisplayScreenStateMap
  mirroringLoading: DisplayScreenStateMap
  pushToScreenLoading: DisplayScreenStateMap
  onToggleCollapse: (value: boolean) => void
  onToggleSection: (sessionKey: string) => void
  onMirroring: (displaySession: DisplayScreen) => void
  onPushToScreen: (displaySession: DisplayScreen) => void
}

function ExpandedPanel({
  openSections,
  mirroringScreens,
  mirroringLoading,
  pushToScreenLoading,
  onToggleCollapse,
  onToggleSection,
  onMirroring,
  onPushToScreen,
}: ExpandedPanelProps) {
  const displayScreens = useSelector(selectDisplayScreens)

  return (
    <div className="w-full max-w-[286px] rounded-lg bg-[rgba(0,0,0,0.5)] shadow-[0_8px_24px_0_rgba(0,0,0,0.25)] backdrop-blur-[12px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <h1 className="lg text-[16px] font-semibold leading-[19.2px] text-white">
          Available Screens
        </h1>
        <button type="button" onClick={() => onToggleCollapse(true)}>
          <CollapseSvg size="m" width="24" height="24" strokeColor="white" />
        </button>
      </div>

      <hr className="bg-[#F8F9F6] opacity-25" />

      {/* Display Screens Sections */}
      <div className="space-y-4 p-4">
        {displayScreens.map((displayScreen) => (
          <div
            key={displayScreen.masterKey}
            className="overflow-hidden rounded border border-[rgba(255,255,255,0.25)]"
          >
            {/* Header */}
            <button
              type="button"
              onClick={() => onToggleSection(displayScreen.masterKey)}
              className="flex w-full items-center justify-between bg-[rgba(255,255,255,0.20)] px-[10px] py-2 transition-colors"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="relative shrink-0">
                  <MonitorSvg {...monitorIconProps} />
                  {mirroringScreens[displayScreen.masterKey] && (
                    <div className="absolute -left-1 -top-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-white bg-[#48A645]">
                      <CheckSvg {...checkIconProps} />
                    </div>
                  )}
                </div>

                <span
                  className="flex-1 truncate text-left text-[16px] font-semibold leading-[19.2px] text-white"
                  title={displayScreen.label}
                >
                  {displayScreen.label}
                </span>
              </div>

              <div className="ml-2 shrink-0">
                <ChevronSvg
                  {...chevronIconProps}
                  rotate={
                    openSections[displayScreen.masterKey] ? 'top' : 'down'
                  }
                />
              </div>
            </button>

            {/* Collapsible Content */}
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                openSections[displayScreen.masterKey]
                  ? 'max-h-[50px] opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <DisplayScreenActions
                displayScreen={displayScreen}
                mirroringScreens={mirroringScreens}
                mirroringLoading={mirroringLoading}
                pushToScreenLoading={pushToScreenLoading}
                onMirroring={onMirroring}
                onPushToScreen={onPushToScreen}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ExpandedPanel
